import type { TextStyle, ViewStyle } from 'react-native';
import { RADIUS, SPACE } from './spacing';
import { TYPE } from './typography';

/**
 * Геометрия управляющих элементов экрана «Чаты» и модалок — одни и те же числа для всех кнопок.
 *
 * Жалоба была буквально «кнопки в меню разные стили»: на одной поверхности сходились высоты
 * 28/30/32/34/~38/~42, пять радиусов (8/10/12/14/16), две толщины рамки (1 и «рамки нет вовсе»
 * у `modalBtn`), три размера иконки (18/20/22) и семь пар кегль+насыщенность. Каждое значение
 * само по себе выглядело осмысленным, но рядом друг с другом они читались как куски разных
 * приложений.
 *
 * Здесь только числа и ни одного цвета: цветовая семантика тем живёт в `chatListPalette.ts`
 * и остаётся нетронутой — общими становятся размеры, а не оттенки.
 *
 * СОЗНАТЕЛЬНЫЕ ИСКЛЮЧЕНИЯ (не «забыли», а отдельный класс элементов):
 * - `actionIcon` в панели дружины — круглая кнопка-действие 38/r19: она читается как аватарный
 *   ряд действий над контактом, а не как кнопка формы, и стоит вне рядов пилюль;
 * - `retentionTimerPressable` — капсула-индикатор (r999, рамка `hairlineWidth`): это в первую
 *   очередь бейдж со счётчиком, нажатие у него вторично.
 * Всё остальное интерактивное на экране «Чаты» и в модалках собирается из констант ниже.
 */
export const CONTROL = {
  /** Основная «пилюля» рядов и квадратная иконочная кнопка шапки. */
  heightM: 36,
  /** Компактный чип внутри панелей и модалок (дружина, тумблеры каналов/групп). */
  heightS: 28,
  /**
   * Единственный радиус интерактивного элемента — та же ступень, что у карточки списка.
   *
   * <p>Было 10: значение вне сетки, из-за которого кнопка на карточке скруглялась чуть иначе,
   * чем сама карточка. Разница в два пикселя не читается как решение, но и не проходит мимо
   * глаза — ровно тот случай, когда «почти одинаково» хуже, чем одинаково.
   */
  radius: RADIUS.md,
  /** Одна толщина рамки для всех кнопок, собранных отсюда, включая модальные. */
  borderWidth: 1,
  /** Иконка/глиф внутри heightM. */
  iconM: 20,
  /** Иконка/глиф внутри heightS. */
  iconS: 16,
  /** Просвет между соседними кнопками в ряду. */
  gapRow: SPACE.md,
  /** Просвет между иконкой и подписью внутри кнопки. */
  gapIconText: SPACE.sm,
  /** Поля подписи внутри пилюли. Было 14 — вне сетки и теснее к подписи, чем нужно. */
  padH: SPACE.md,
  padHS: SPACE.md,
  /**
   * Вертикальный запас. Высота задана через `minHeight`, а не жёстким `height`: подпись кнопки
   * локализуется (ru/sr/fr длиннее en) и на узком телефоне переносится на вторую строку —
   * фиксированная высота обрезала бы её, а `minHeight` даёт ту же ровную сетку в обычном случае
   * и позволяет кнопке подрасти в исключительном.
   */
  padV: 4,
  /** Подпись кнопки-действия — ступень `footnote` шкалы. */
  fontM: TYPE.footnote.fontSize,
  /** Подпись чипа/вкладки — ступень `micro` шкалы. */
  fontS: TYPE.micro.fontSize,
  /**
   * Глиф, нарисованный текстом там, где рядом стоят настоящие иконки: стрелка ▾ на кнопке
   * дружины, «×» в шапке модалки. Раньше они шли кеглем 18 и 16 — рядом с иконкой 20 px оба
   * читались случайными.
   */
  glyph: 16,
  weight: '700',
} as const;

/** Горизонтальные поля экрана: шапка, ряды кнопок и строки списка стоят по одной линии. */
export const SCREEN_PAD_H = SPACE.lg;

/** Общее для любой кнопки: рамка, радиус, центрированное содержимое. */
const controlBase: ViewStyle = {
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: CONTROL.radius,
  borderWidth: CONTROL.borderWidth,
};

/** Широкая кнопка-«пилюля»: кнопки дружины/групп/каналов, чипы-фильтры, модальные кнопки. */
export const controlPill: ViewStyle = {
  ...controlBase,
  flexDirection: 'row',
  gap: CONTROL.gapIconText,
  minHeight: CONTROL.heightM,
  paddingVertical: CONTROL.padV,
  paddingHorizontal: CONTROL.padH,
};

/** Квадратная иконочная кнопка: шапка списка чатов, «×» в модалке настроек. */
export const controlIconBtn: ViewStyle = {
  ...controlBase,
  width: CONTROL.heightM,
  height: CONTROL.heightM,
};

/** Компактный чип внутри панели или модалки. */
export const controlChipS: ViewStyle = {
  ...controlBase,
  flexDirection: 'row',
  gap: CONTROL.gapIconText,
  minHeight: CONTROL.heightS,
  paddingVertical: 0,
  paddingHorizontal: CONTROL.padHS,
};

/** Типографика подписи на heightM-кнопке (цвет добавляет вызывающая сторона из палитры). */
export const controlTextM: TextStyle = { fontSize: CONTROL.fontM, fontWeight: CONTROL.weight };

/** Типографика подписи на чипе/вкладке. */
export const controlTextS: TextStyle = { fontSize: CONTROL.fontS, fontWeight: CONTROL.weight };

/**
 * Текстовый глиф вместо иконки. `lineHeight` равен размеру иконки: иначе строка глифа
 * оказывается выше картинки рядом и одна задаёт высоту кнопки.
 */
export const controlGlyphText: TextStyle = {
  fontSize: CONTROL.glyph,
  lineHeight: CONTROL.iconM,
  fontWeight: CONTROL.weight,
};
